import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { PredominantDegreeCode } from '@backrs_ui/common-ui/src/models/college';
import { NuggetVariant } from '@backrs_ui/common-ui/src/models/nugget';
import { ScholarshipAwardType } from '@backrs_ui/common-ui/src/models/scholarship';
import { getToolTypeLabel, ToolType } from '@backrs_ui/common-ui/src/models/tool';

import { NuggetBadgeProps } from './NuggetBadge';

type BadgeMappingResult = Readonly<{
  label: string;
  iconConfig: NuggetBadgeProps['iconConfig'];
}>;

const DEGREE_CODE_BADGE_LABEL: Record<PredominantDegreeCode, string> = {
  bachelors: 'University',
  graduate: 'University',
  associates: '2-Year College',
  certificate: 'Trades/Technical School',
  not_classified: 'College',
};

/**
 * Maps institution predominant_degree_code to a specific badge label.
 */
export const getInstitutionBadgeLabel = (predominantDegreeCode?: PredominantDegreeCode): string =>
  (predominantDegreeCode && DEGREE_CODE_BADGE_LABEL[predominantDegreeCode]) || 'College';

const DEGREE_CODE_BADGE_ICON: Record<PredominantDegreeCode, AppIcons> = {
  bachelors: 'Building08',
  graduate: 'Building08',
  associates: 'FileShield02',
  certificate: 'Tool01',
  not_classified: 'GraduationHat02',
};

/**
 * Maps institution predominant_degree_code to a specific badge icon.
 */
export const getInstitutionBadgeIcon = (predominantDegreeCode?: PredominantDegreeCode): AppIcons =>
  (predominantDegreeCode && DEGREE_CODE_BADGE_ICON[predominantDegreeCode]) || 'GraduationHat02';

/**
 * Variants where the badge icon uses the Backrs logo instead of an AppIcon.
 * These are Backrs-branded content types.
 */
const BACKRS_LOGO_VARIANTS = new Set<NuggetVariant>([
  'deep-dive',
  'playlist',
  'tool',
  'solution-session',
]);

/**
 * Maps variants that show a featured icon overlay to their background ColorToken.
 */
export const FEATURED_ICON_BG_COLOR_MAP: Partial<Record<NuggetVariant, ColorToken>> = {
  'deep-dive': 'Brand/600',
  playlist: 'Brand/800',
  tool: 'Brand/500',
  'solution-session': 'Brand/50',
  'career-path': 'Brand/600',
  major: 'Error/400',
  competency: 'Brand/200',
  'educational-investment': 'Brand/800',
  industry: 'Brand/50',
  'career-track': 'Brand/600',
};

/**
 * Default AppIcon mapping per variant for the badge.
 */
const VARIANT_BADGE_ICON_MAP: Partial<Record<NuggetVariant, AppIcons>> = {
  article: 'File06',
  'video-external': 'PlayCircle',
  'video-internal': 'PlayCircle',
  opportunity: 'ArrowCircleBrokenRight',
  scholarships: 'CurrencyDollarCircle',
  employer: 'Building06',
  institution: 'CurrencyDollarCircle',
  'career-path': 'Briefcase01',
  major: 'Certificate01',
  competency: 'CheckVerified03',
  'educational-investment': 'Building08',
  industry: 'Building07',
  coach: 'User02',
  event: 'CalendarPlus01',
  audio: 'VolumeMin',
  document: 'File06',
  book: 'BookOpen01',
  resource: 'BookOpen01',
  'text-response': 'BookOpen01',
  'generated-content': 'File02',
  'career-track': 'Rocket02',
  'track-assessment-result': 'File02',
  'track-assessment-unlock': 'LockKeyholeSquare',
  'profile-external': 'User02',
  'profile-internal': 'User02',
  internship: 'Briefcase01',
  post: 'Edit05',
};

/**
 * Default label mapping per variant for the badge.
 */
const VARIANT_BADGE_LABEL_MAP: Partial<Record<NuggetVariant, string>> = {
  article: 'Article',
  'video-external': 'Video',
  'video-internal': 'Video',
  opportunity: 'Opportunity',
  scholarships: 'Scholarship',
  employer: 'Employer',
  'career-path': 'Career Path',
  major: 'College Major',
  competency: 'Competency',
  'educational-investment': 'Educational Investment',
  industry: 'Industry',
  coach: 'Coach',
  'deep-dive': 'Deep Dive',
  playlist: 'Playbook',
  tool: 'Tool',
  'solution-session': 'Solution Session',
  event: 'Event',
  audio: 'Audio',
  document: 'Document',
  book: 'Book',
  resource: 'Resource',
  'text-response': 'Resource',
  'generated-content': 'For You',
  'career-track': 'Career Track',
  'track-assessment-result': 'For You',
  'track-assessment-unlock': 'Four Unlocks',
  'profile-external': 'Profile',
  'profile-internal': 'Profile',
  internship: 'Internship',
  post: 'Post',
};

type GetNuggetCardBadgeParams = Readonly<{
  variant: NuggetVariant;
  /** For institution variant, the predominant degree code (e.g. 'bachelors', 'associates') */
  predominantDegreeCode?: PredominantDegreeCode;
  /** For text-response variant (resume), the user's profile image URL */
  profileImageUrl?: string;
  /** For generated-content variant, the formatted date string for the badge label */
  formattedDate?: string;
  /** For internship variant, the `role_type` string from `internship_info` */
  roleType?: string;
  /** For scholarships variant, the `award_type` from `scholarship_info` */
  awardType?: ScholarshipAwardType;
  /** For tool variant, the `tool_type` from `tool_info` (drives the Tool/Guide/... tag) */
  toolType?: ToolType;
}>;

export const INTERNSHIP_ROLE_LABEL_MAP: Record<string, string> = {
  'Junior IC': 'Full-time',
  Intern: 'Internship',
};

export const getInternshipRoleLabel = (roleType: string | undefined): string | undefined =>
  roleType ? (INTERNSHIP_ROLE_LABEL_MAP[roleType] ?? roleType) : undefined;

/**
 * Returns badge props for a given nugget variant.
 */
export const getNuggetCardBadge = ({
  variant,
  predominantDegreeCode,
  profileImageUrl,
  formattedDate,
  roleType,
  awardType,
  toolType,
}: GetNuggetCardBadgeParams): BadgeMappingResult => {
  // Tool-backed nuggets all share the `tool` variant; tool_type drives the tag label.
  if (variant === 'tool') {
    return {
      label: getToolTypeLabel(toolType),
      iconConfig: { mode: 'backrsLogo' },
    };
  }

  // text-response (resume) uses the user's profile avatar as badge icon
  if (variant === 'text-response' && profileImageUrl) {
    return {
      label: 'Resume',
      iconConfig: { mode: 'avatar', avatarUrl: profileImageUrl },
    };
  }

  // `internship` variant covers both internships and jobs; `role_type` distinguishes.
  if (variant === 'internship') {
    return {
      label: getInternshipRoleLabel(roleType) ?? 'Opportunity',
      iconConfig: { mode: 'appIcon', icon: VARIANT_BADGE_ICON_MAP.internship ?? 'Briefcase01' },
    };
  }

  // Scholarships show the specific award_type (Scholarship, Grant, Prize, Fellowship, Forgivable Loan).
  if (variant === 'scholarships') {
    return {
      label: awardType ?? VARIANT_BADGE_LABEL_MAP.scholarships ?? 'Scholarship',
      iconConfig: {
        mode: 'appIcon',
        icon: VARIANT_BADGE_ICON_MAP.scholarships ?? 'CurrencyDollarCircle',
      },
    };
  }

  // generated-content uses MessageSquare02 icon with a date label
  if (variant === 'generated-content' && formattedDate) {
    return {
      label: formattedDate,
      iconConfig: { mode: 'appIcon', icon: 'MessageSquare02' },
    };
  }

  // Backrs-branded variants use the Backrs logo
  if (BACKRS_LOGO_VARIANTS.has(variant)) {
    return {
      label:
        VARIANT_BADGE_LABEL_MAP[variant] ??
        variant
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
      iconConfig: { mode: 'backrsLogo' },
    };
  }

  // Institution uses degree-based label and icon
  if (variant === 'institution') {
    return {
      label: getInstitutionBadgeLabel(predominantDegreeCode),
      iconConfig: { mode: 'appIcon', icon: getInstitutionBadgeIcon(predominantDegreeCode) },
    };
  }

  // Default: AppIcon mode
  const icon = VARIANT_BADGE_ICON_MAP[variant] ?? 'InfoCircle';
  const label =
    VARIANT_BADGE_LABEL_MAP[variant] ??
    variant
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  if (
    process.env.NODE_ENV === 'development' &&
    (!VARIANT_BADGE_ICON_MAP[variant] || !VARIANT_BADGE_LABEL_MAP[variant])
  ) {
    console.warn(`[NuggetCard] No badge mapping for variant "${variant}" — using fallback`);
  }

  return {
    label,
    iconConfig: { mode: 'appIcon', icon },
  };
};

/**
 * Returns the featured icon bg ColorToken for a variant, or undefined if no featured icon.
 */
export const getFeaturedIconBgColor = (variant: NuggetVariant): ColorToken | undefined =>
  FEATURED_ICON_BG_COLOR_MAP[variant];
